import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaArrowRight, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import StatCard from '../shared/ui/StatCard';
import styles from './DashboardPage.module.css';
import { API_URL } from '../config/env';
import { getSettlementDatesWithVouchers } from '../services/settlementDateService';

const STATUS_ORDER = ['missing', 'partial', 'complete', 'overpaid', 'closed'];
const STATUS_META = {
  missing: { label: 'Missing', color: '#dc2626' },
  partial: { label: 'Partial', color: '#d97706' },
  complete: { label: 'Complete', color: '#16a34a' },
  overpaid: { label: 'Overpaid', color: '#ca8a04' },
  closed: { label: 'Closed', color: '#64748b' }
};

function formatMoney(n) {
  if (n == null || Number.isNaN(n)) return '—';
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

const LABEL_BAR_COLORS = {
  brand: '#4f46e5',
  success: '#16a34a',
  info: '#2563eb',
  warning: '#d97706',
  danger: '#dc2626',
  accent: '#0d9488',
  neutral: '#64748b'
};

function labelColorToHex(color) {
  if (!color) return LABEL_BAR_COLORS.neutral;
  const key = String(color).toLowerCase();
  return LABEL_BAR_COLORS[key] || LABEL_BAR_COLORS.neutral;
}

export default function DashboardPage() {
  const [summary, setSummary] = useState(null);
  const [unmatchedVoucherStudents, setUnmatchedVoucherStudents] = useState([]);
  const [labelsReceived, setLabelsReceived] = useState([]);
  const [voucherPeriodSummary, setVoucherPeriodSummary] = useState([]);
  const [missingStudents, setMissingStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [periodsWithData, setPeriodsWithData] = useState([]);
  const [selectedSettlementId, setSelectedSettlementId] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const periods = await getSettlementDatesWithVouchers();
        if (cancelled) return;
        const list = Array.isArray(periods) ? periods : [];
        setPeriodsWithData(list);
        const firstId = list[0]?.id;
        setSelectedSettlementId(firstId != null ? String(firstId) : '');
        if (list.length === 0) {
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setPeriodsWithData([]);
          setSelectedSettlementId('');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadDashboardForPeriod = useCallback(async (settlementDateId) => {
    setLoading(true);
    setError(null);
    const q = settlementDateId ? `?settlement_date_id=${encodeURIComponent(settlementDateId)}` : '';
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [summaryRes, labelsRes, voucherPeriodRes, unmatchedRes, missingRes] = await Promise.all([
        fetch(`${API_URL}/api/dashboard/summary${q}`, { headers }),
        fetch(`${API_URL}/api/dashboard-extra/labels-received${q}`, { headers }),
        fetch(`${API_URL}/api/dashboard-voucher/period-summary${q}`, { headers }),
        fetch(`${API_URL}/api/dashboard-details/unmatched-voucher-students${q}`, { headers }),
        fetch(`${API_URL}/api/dashboard-details/missing-students${q}`, { headers })
      ]);
      if (!summaryRes.ok) throw new Error('Failed to load dashboard summary');
      if (!labelsRes.ok) throw new Error('Failed to load label totals');
      if (!voucherPeriodRes.ok) throw new Error('Failed to load period summary');
      if (!unmatchedRes.ok) throw new Error('Failed to load unmatched voucher rows');
      if (!missingRes.ok) throw new Error('Failed to load students missing vouchers');
      const sum = await summaryRes.json();
      setSummary(sum && Object.keys(sum).length ? sum : null);
      setLabelsReceived(await labelsRes.json());
      setVoucherPeriodSummary(await voucherPeriodRes.json());
      setUnmatchedVoucherStudents(await unmatchedRes.json());
      setMissingStudents(await missingRes.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedSettlementId) return;
    loadDashboardForPeriod(selectedSettlementId);
  }, [selectedSettlementId, loadDashboardForPeriod]);

  const periodIndex = useMemo(() => {
    const i = periodsWithData.findIndex((p) => String(p.id) === String(selectedSettlementId));
    return i >= 0 ? i : 0;
  }, [periodsWithData, selectedSettlementId]);

  const goOlderPeriod = useCallback(() => {
    const next = periodsWithData[periodIndex + 1];
    if (next != null) setSelectedSettlementId(String(next.id));
  }, [periodsWithData, periodIndex]);

  const goNewerPeriod = useCallback(() => {
    const next = periodsWithData[periodIndex - 1];
    if (next != null) setSelectedSettlementId(String(next.id));
  }, [periodsWithData, periodIndex]);

  const canGoOlder = periodIndex < periodsWithData.length - 1;
  const canGoNewer = periodIndex > 0 && periodsWithData.length > 0;

  const latestPeriod = useMemo(() => {
    const rows = voucherPeriodSummary || [];
    return rows[0] || null;
  }, [voucherPeriodSummary]);

  const sortedStatuses = useMemo(() => {
    const rows = summary?.statusBreakdown || [];
    const byKey = {};
    rows.forEach(s => {
      byKey[s.status] = Number(s.count) || 0;
    });
    const ordered = STATUS_ORDER.filter(k => k in byKey && byKey[k] > 0).map(status => ({
      status,
      count: byKey[status],
      ...STATUS_META[status]
    }));
    const extras = Object.keys(byKey).filter(k => !STATUS_ORDER.includes(k) && byKey[k] > 0).map(status => ({
      status,
      count: byKey[status],
      label: status,
      color: '#94a3b8'
    }));
    return [...ordered, ...extras];
  }, [summary]);

  const statusBreakdownMax = useMemo(() => {
    if (!sortedStatuses.length) return 1;
    return Math.max(...sortedStatuses.map((s) => s.count), 1);
  }, [sortedStatuses]);

  const attentionCount = useMemo(() => {
    const breakdown = summary?.statusBreakdown || [];
    let n = 0;
    breakdown.forEach(s => {
      if (['missing', 'partial', 'overpaid'].includes(s.status)) {
        n += Number(s.count) || 0;
      }
    });
    return n;
  }, [summary]);

  const recoveryPct = useMemo(() => {
    const exp = Number(latestPeriod?.totalExpected);
    const act = Number(latestPeriod?.totalActual);
    if (!exp || exp <= 0) return null;
    return Math.min(100, Math.round((act / exp) * 100));
  }, [latestPeriod]);

  const latestSettlementRow = summary?.recentSettlementDates?.[0];

  const viewingPayoutDateStr = useMemo(() => {
    const p = periodsWithData.find((x) => String(x.id) === String(selectedSettlementId));
    if (!p?.settlement_date) return null;
    return String(p.settlement_date).slice(0, 10);
  }, [periodsWithData, selectedSettlementId]);

  const labelReceivedRows = useMemo(() => {
    return (labelsReceived || [])
      .filter((r) => Number(r.total_received) > 0)
      .map((r) => ({
        id: r.label_id,
        name: r.name || 'Label',
        value: Number(r.total_received) || 0,
        color: labelColorToHex(r.color)
      }));
  }, [labelsReceived]);

  const labelReceivedMax = useMemo(() => {
    if (!labelReceivedRows.length) return 1;
    return Math.max(...labelReceivedRows.map((r) => r.value), 1);
  }, [labelReceivedRows]);

  if (loading) {
    return (
      <div className={styles.shell}>
        <div className={`${styles.shellInner} ${styles.stateMessage}`}>
          <div className={styles.loading}>Loading reconciliation overview…</div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className={styles.shell}>
        <div className={`${styles.shellInner} ${styles.stateMessage}`}>
          <div className={styles.error}>{error}</div>
        </div>
      </div>
    );
  }

  if (!summary || !latestPeriod || summary.students === undefined) {
    return (
      <div className={styles.shell}>
        <div className={`${styles.shellInner} ${styles.stateMessage}`}>
          <div className={styles.emptyState}>
            <p className={styles.emptyStateTitle}>No settlement period with voucher data yet</p>
            <p style={{ margin: '0 0 16px', lineHeight: 1.5 }}>
              Import vouchers tied to a settlement date to see reconciliation, amounts, and follow-ups here.
            </p>
            <Link className={styles.linkBtn} to="/vouchers">
              Open vouchers workspace <FaArrowRight aria-hidden />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const denom = Math.max(
    Number(latestPeriod.totalExpected) || 0,
    Number(latestPeriod.totalActual) || 0,
    Number(latestPeriod.overpaidSum) || 0,
    1
  );
  const expectedW = (Number(latestPeriod.totalExpected) / denom) * 100;
  const actualW = (Number(latestPeriod.totalActual) / denom) * 100;
  const overpaidW = (Number(latestPeriod.overpaidSum) / denom) * 100;

  const topMissing = (missingStudents || []).slice(0, 14);
  const unmatchedPaymentTotal = unmatchedVoucherStudents.reduce(
    (sum, row) => sum + (Number(row.voucher_count) || 0),
    0
  );

  return (
    <div className={styles.shell}>
      <div className={styles.shellInner}>
        <header className={styles.hero}>
          <div>
          <p className={styles.heroEyebrow}>Current settlement snapshot</p>
          <h1 className={styles.heroTitle}>
            Voucher reconciliation
            {latestPeriod.date_range ? (
              <span className={styles.periodPicker} role="group" aria-label="Settlement period">
                <button
                  type="button"
                  className={styles.periodArrow}
                  onClick={goOlderPeriod}
                  disabled={!canGoOlder}
                  aria-label="Older settlement period"
                >
                  <FaChevronLeft aria-hidden />
                </button>
                <span className={styles.periodBadge}>{latestPeriod.date_range}</span>
                <button
                  type="button"
                  className={styles.periodArrow}
                  onClick={goNewerPeriod}
                  disabled={!canGoNewer}
                  aria-label="Newer settlement period"
                >
                  <FaChevronRight aria-hidden />
                </button>
              </span>
            ) : null}
          </h1>
          <p className={styles.heroMeta}>
            {viewingPayoutDateStr
              ? `Settlement payout date for this view: ${viewingPayoutDateStr}. Compare funding received vs expected childcare rates across active students.`
              : latestSettlementRow
              ? `Most recent payout date recorded: ${String(latestSettlementRow.settlement_date || '').slice(0, 10)}. Compare funding received vs expected childcare rates across active students.`
              : 'Compare funding received vs expected rates across active students for the loaded period.'}
          </p>
          </div>
          <div className={styles.heroActions}>
            <Link className={styles.linkBtn} to="/vouchers">
              Vouchers <FaArrowRight aria-hidden />
            </Link>
            <Link className={styles.linkBtn} to="/students">
              Directory <FaArrowRight aria-hidden />
            </Link>
            <Link className={styles.linkBtn} to="/rates">
              Rates <FaArrowRight aria-hidden />
            </Link>
          </div>
        </header>

        <section className={styles.kpiGrid}>
          <StatCard
            label="Collected this period"
            value={formatMoney(latestPeriod.totalActual)}
            delta={latestPeriod.date_range ? `Range ${latestPeriod.date_range}` : null}
            tone="accent"
          />
          <StatCard
            label="Expected (rates × days)"
            value={formatMoney(latestPeriod.totalExpected)}
            delta={
              recoveryPct != null ? `${recoveryPct}% of expected received` : 'Set rates to refine expected totals'
            }
            tone="brand"
          />
          <StatCard
            label="Students needing follow-up"
            value={attentionCount.toLocaleString()}
            delta={`${(latestPeriod.missingCount ?? 0)} with zero voucher`}
            tone={attentionCount > 0 ? 'warning' : 'success'}
          />
          <StatCard
            label="Voucher roster gaps"
            value={unmatchedVoucherStudents.length.toLocaleString()}
            delta={
              unmatchedVoucherStudents.length
                ? `${unmatchedPaymentTotal.toLocaleString()} payments with no matching student`
                : 'Every payment is linked to a student'
            }
            tone={unmatchedVoucherStudents.length ? 'danger' : 'success'}
          />
        </section>

        <div className={styles.workspace}>
          <div className={styles.chartStack}>
            <div className={`${styles.panel} ${styles.panelMuted}`}>
              <h2 className={styles.panelTitle}>Funds vs expectation</h2>
              <p className={styles.panelSubtitle}>
                Expected subsidy from rate rules, voucher totals received, and overpayment delta for this period.
              </p>
              <div className={styles.barCompare}>
                <div className={styles.compareRow}>
                  <div className={styles.compareLabelRow}>
                    <span className={styles.compareLabel}>Expected</span>
                    <span className={styles.compareValue}>{formatMoney(latestPeriod.totalExpected)}</span>
                  </div>
                  <div className={styles.compareTrack}>
                    <div className={styles.compareFill} style={{ width: `${expectedW}%`, background: 'linear-gradient(90deg, #4f46e5, #6366f1)' }} />
                  </div>
                </div>
                <div className={styles.compareRow}>
                  <div className={styles.compareLabelRow}>
                    <span className={styles.compareLabel}>Received</span>
                    <span className={styles.compareValue}>{formatMoney(latestPeriod.totalActual)}</span>
                  </div>
                  <div className={styles.compareTrack}>
                    <div className={styles.compareFill} style={{ width: `${actualW}%`, background: 'linear-gradient(90deg, #16a34a, #22c55e)' }} />
                  </div>
                </div>
                <div className={styles.compareRow}>
                  <div className={styles.compareLabelRow}>
                    <span className={styles.compareLabel}>Sum overpaid (above expected)</span>
                    <span className={styles.compareValue}>{formatMoney(latestPeriod.overpaidSum)}</span>
                  </div>
                  <div className={styles.compareTrack}>
                    <div className={styles.compareFill} style={{ width: `${overpaidW}%`, background: 'linear-gradient(90deg, #eab308, #facc15)' }} />
                  </div>
                </div>
                <p className={styles.compareHint}>
                  {(latestPeriod.bonusCount ?? 0)} student{(latestPeriod.bonusCount ?? 0) !== 1 ? 's have' : ' has'} received
                  more than expected for this period — review closures or duplicated lines if that looks wrong.
                </p>
              </div>
            </div>

            <div className={`${styles.panel} ${styles.panelMuted}`}>
              <h2 className={styles.panelTitle}>Student status breakdown</h2>
              <p className={styles.panelSubtitle}>
                Per-student reconciliation for {summary.voucherStatusDateRange || latestPeriod.date_range}. Closures
                override calculated status.
              </p>
              <div className={styles.barCompare}>
                {sortedStatuses.length ? (
                  <>
                    {sortedStatuses.map((s) => {
                      const w = (s.count / statusBreakdownMax) * 100;
                      const fillBg = `linear-gradient(90deg, ${s.color}, color-mix(in srgb, ${s.color} 72%, white))`;
                      return (
                        <div key={s.status} className={styles.compareRow}>
                          <div className={styles.compareLabelRow}>
                            <span className={styles.compareLabel}>{s.label}</span>
                            <span className={styles.compareValue}>{s.count.toLocaleString()}</span>
                          </div>
                          <div className={styles.compareTrack}>
                            <div
                              className={styles.compareFill}
                              style={{ width: `${w}%`, background: fillBg }}
                            />
                          </div>
                        </div>
                      );
                    })}
                    <p className={styles.compareHint}>
                      Bar length scales to the largest status count ({statusBreakdownMax.toLocaleString()} students).
                    </p>
                  </>
                ) : (
                  <p className={styles.sideMuted}>No status breakdown for this period.</p>
                )}
              </div>
            </div>

            <div className={`${styles.panel} ${styles.panelMuted}`}>
              <h2 className={styles.panelTitle}>Received by student label</h2>
              <p className={styles.panelSubtitle}>
                Sum of voucher totals for this period, attributed to students with each directory tag (students with
                multiple tags contribute to each tag).
              </p>
              <div className={styles.barCompare}>
                {labelReceivedRows.length ? (
                  <>
                    {labelReceivedRows.map((row) => {
                      const w = (row.value / labelReceivedMax) * 100;
                      const fillBg = `linear-gradient(90deg, ${row.color}, color-mix(in srgb, ${row.color} 72%, white))`;
                      return (
                        <div key={row.id ?? row.name} className={styles.compareRow}>
                          <div className={styles.compareLabelRow}>
                            <span className={styles.compareLabel}>{row.name}</span>
                            <span className={styles.compareValue}>{formatMoney(row.value)}</span>
                          </div>
                          <div className={styles.compareTrack}>
                            <div
                              className={styles.compareFill}
                              style={{ width: `${w}%`, background: fillBg }}
                            />
                          </div>
                        </div>
                      );
                    })}
                    <p className={styles.compareHint}>
                      Bar length scales to the largest label total this period; amounts are not additive across tags when
                      students share multiple labels.
                    </p>
                  </>
                ) : (
                  <p className={styles.sideMuted}>
                    No labeled students with voucher receipts for this period, or no student/both labels in use.
                  </p>
                )}
              </div>
            </div>
          </div>

          <aside className={styles.sideStack}>
            <div className={[styles.sidePanel, styles.sidePanelDanger].join(' ')}>
              <div className={styles.sideHeadingRow}>
                <h3 className={styles.sideHeading}>Payments without a student</h3>
                <span className={styles.sideCount}>{unmatchedVoucherStudents.length.toLocaleString()}</span>
              </div>
              {unmatchedVoucherStudents.length === 0 ? (
                <p className={styles.listOk}>Every payment is linked to a student.</p>
              ) : (
                <>
                  <p className={styles.sideMuted}>
                    {unmatchedPaymentTotal.toLocaleString()} payments in this period did not match a student CIN.
                  </p>
                  <ul className={`${styles.list} ${styles.listScroll}`}>
                    {unmatchedVoucherStudents.map((row) => (
                      <li key={`${row.cin || ''}-${row.child_name}`}>
                        <span className="id">{row.child_name || 'Unknown child'}</span>
                        {row.cin ? (
                          <span className={styles.sideCase}>CIN {row.cin}</span>
                        ) : null}
                        <span className={styles.sideRowCount}>{Number(row.voucher_count) || 0}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            <div className={[styles.sidePanel, styles.sidePanelAccent].join(' ')}>
              <h3 className={styles.sideHeading}>Profiles with no voucher (this period)</h3>
              <p className={styles.sideMuted}>
                Showing {topMissing.length} of {missingStudents.length}. Open the directory or vouchers workspace for more detail.
              </p>
              {topMissing.length === 0 ? (
                <p className={styles.listOk}>Everyone on file appears to have voucher coverage for this period.</p>
              ) : (
                <ul className={`${styles.list} ${styles.listScroll}`}>
                  {topMissing.map(s => (
                    <li key={s.student_id}>
                      <span className="id">{s.student_id}</span>
                      <span>{[s.first_name, s.last_name].filter(Boolean).join(' ')}</span>
                    </li>
                  ))}
                </ul>
              )}
              {missingStudents.length > topMissing.length ? (
                <p className={styles.sideMuted} style={{ marginTop: 12, marginBottom: 0 }}>
                  +{missingStudents.length - topMissing.length} more in the dataset.
                </p>
              ) : null}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
